import { t, useLocale } from "@/i18n/locale";
import { Message } from "@/i18n/Message";
/** Legacy saved-app routes resolve an authorized new-tab link, never an iframe. */

import { lazy, Suspense, useEffect, useRef, useState } from "react";

import { appLinkProps } from "./openApp";
import { computeClient } from "@/components/personal-apps/computeClient";
import type { PersonalApp } from "@/components/personal-apps/types";
import AppSplash from "./discovery/AppSplash";
import LaunchProgress from "./discovery/LaunchProgress";
const NativeInstanceSurface = lazy(() => import("./NativeInstanceSurface"));
import InstanceFilesAction from "./InstanceFilesAction";
import ProcessPreparation, { hasWanPreparation } from "./ProcessPreparation";

export default function InstanceLaunch({
  instanceId,
  onBack,
  onResolved,
  name,
  hideLoading = false,
  onFailure,
}: {
  instanceId: string;
  onBack: () => void;
  onResolved?: (app: PersonalApp) => void;
  /** The Instance could not be resolved (deleted/foreign/temporary). Lets a
   *  surrounding surface replace its Splash with this error state. */
  onFailure?: () => void;
  /** The app's display name for the Default Splash, when the caller knows it. */
  name?: string;
  /** The surrounding surface already owns the single launch Splash/progress
   *  (Discover does); render nothing here instead of a second loading UI. */
  hideLoading?: boolean;
}) {
  useLocale();
  const [app, setApp] = useState<PersonalApp | null>(null);
  const [failed, setFailed] = useState(false);
  const resolvedCallback = useRef(onResolved);
  resolvedCallback.current = onResolved;
  const failureCallback = useRef(onFailure);
  failureCallback.current = onFailure;

  useEffect(() => {
    const controller = new AbortController();
    setApp(null);
    setFailed(false);
    void Promise.resolve()
      .then(() => controller.signal.aborted ? null : computeClient.get(instanceId, controller.signal))
      .then((result) => {
        if (result && !controller.signal.aborted) {
          if (result.instance.instance_id !== instanceId || result.instance.status !== "active" || result.instance.public_try_expires_at != null) {
            setFailed(true);
            failureCallback.current?.();
            return;
          }
          setApp(result.instance);
          resolvedCallback.current?.(result.instance);
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setFailed(true);
          failureCallback.current?.();
        }
      });
    return () => {
      controller.abort();
    };
  }, [instanceId]);

  if (failed) {
    return (
      <div className="session-console">
        <div className="session-console-stage">
          <div className="empty-state" role="alert">
            <p><Message message={"This app isn't available."} /></p>
            <button type="button" className="btn btn--primary" onClick={onBack}><Message message={"Back"} /></button>
          </div>
        </div>
      </div>
    );
  }

  if (!app || app.instance_id !== instanceId) {
    if (hideLoading) return <div className="session-console" />;
    return (
      <div className="session-console instance-launch-pending">
        <LaunchProgress active complete={false} className="launch-progress--top" />
        <div className="session-console-stage">
          <AppSplash inline name={name ?? t("App")} />
        </div>
      </div>
    );
  }

  if (app.role === "owner" && app.prepared_import?.realization === "native_oci_container")
    return <Suspense fallback={null}><NativeInstanceSurface key={app.instance_id} app={app} onBack={onBack} /></Suspense>;

  if (hasWanPreparation(app))
    return (
      <div className="session-console">
        <button type="button" className="btn" onClick={onBack}>
          <Message message={"Close"} />
        </button>
        <ProcessPreparation app={app} />
      </div>
    );

  return (
    <div className="session-console">
      <div className="session-console-stage">
        <div className="empty-state">
          {app.role === "editor" ? <InstanceFilesAction app={app} /> : null}
          <a className="btn btn--primary" aria-label={t("Open my app")} {...appLinkProps(app.app_url)}><Message message={"Open {{value0}} in a new tab"} values={{ value0: app.display_name }} /></a>
          <button type="button" className="btn" onClick={onBack}><Message message={"Back to Library"} /></button>
        </div>
      </div>
    </div>
  );
}
